import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  CircularProgress,
  Snackbar,
  MenuItem,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import { useTranslation } from '../../app/LanguageContext';
import { getPurchaseCategoriesSettings, updatePurchaseCategoriesSettings } from '../../app/api';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} id={`op-purchases-settings-tabpanel-${index}`} {...other}>
      {value === index ? <Box sx={{ pt: 2 }}>{children}</Box> : null}
    </div>
  );
}

const emptyRow = () => ({ code: '', label: '' });

export default function OperationalPurchasesSettingsPage() {
  const { translate } = useTranslation();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([emptyRow()]);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [migrationOpen, setMigrationOpen] = useState(false);
  const [pendingMigrations, setPendingMigrations] = useState([]);
  const [migrationTargets, setMigrationTargets] = useState({});

  const newCategoryCodes = useMemo(
    () => rows.map((r) => String(r.code || '').trim().toUpperCase()).filter(Boolean),
    [rows]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPurchaseCategoriesSettings();
      const list = res?.data?.categories ?? res?.categories ?? [];
      if (Array.isArray(list) && list.length > 0) {
        setRows(list.map((c) => ({ code: c.code ?? '', label: c.label ?? '' })));
      } else {
        setRows([emptyRow()]);
      }
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
      setRows([emptyRow()]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const clearMigrationUi = useCallback(() => {
    setMigrationOpen(false);
    setPendingMigrations([]);
    setMigrationTargets({});
  }, []);

  /** Dismiss migration dialog and resync from server (save was not applied). */
  const dismissMigration = useCallback(async () => {
    if (saving) return;
    clearMigrationUi();
    try {
      await load();
    } catch (e) {
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    }
  }, [saving, load, clearMigrationUi]);

  const buildCategoriesPayload = () =>
    rows
      .map((r) => ({ code: String(r.code || '').trim().toUpperCase(), label: String(r.label || '').trim() }))
      .filter((r) => r.label);

  const save = async (categoryMigrations) => {
    setSaving(true);
    try {
      const categories = buildCategoriesPayload();
      const body = { categories };
      if (categoryMigrations && typeof categoryMigrations === 'object') {
        const keys = Object.keys(categoryMigrations);
        if (keys.length > 0) {
          body.category_migrations = categoryMigrations;
        }
      }
      await updatePurchaseCategoriesSettings(body);
      clearMigrationUi();
      setSnackbar({ open: true, message: translate('common.saved'), severity: 'success' });
      try {
        await load();
      } catch (loadErr) {
        setSnackbar({ open: true, message: loadErr.message, severity: 'error' });
      }
    } catch (e) {
      if (e.status === 409 && e.responseData && Array.isArray(e.responseData.pending_migrations)) {
        const pend = e.responseData.pending_migrations;
        const resolved = e.responseData.resolved_categories;
        if (Array.isArray(resolved) && resolved.length > 0) {
          setRows(resolved.map((c) => ({ code: c.code ?? '', label: c.label ?? '' })));
        }
        setPendingMigrations(pend);
        const init = {};
        for (const p of pend) {
          init[p.from_code] = '';
        }
        setMigrationTargets(init);
        setMigrationOpen(true);
        return;
      }
      setSnackbar({ open: true, message: e.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const confirmMigration = async () => {
    const map = {};
    for (const p of pendingMigrations) {
      const to = String(migrationTargets[p.from_code] || '').trim().toUpperCase();
      if (!to) {
        setSnackbar({
          open: true,
          message: translate('operational_purchases.migration_missing_target'),
          severity: 'error',
        });
        return;
      }
      map[p.from_code] = to;
    }
    await save(map);
  };

  const addRow = () => setRows((prev) => [...prev, emptyRow()]);
  const removeRow = (idx) => setRows((prev) => prev.filter((_, i) => i !== idx));

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', p: { xs: 2, sm: 3 } }}>
      <Typography variant="h5" fontWeight={600} gutterBottom>
        {translate('operational_purchases.settings_title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {translate('operational_purchases.settings_subtitle')}
      </Typography>
      <Paper variant="outlined" sx={{ width: '100%' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ px: 2, pt: 1 }}>
          <Tab label={translate('operational_purchases.settings_tab_categories')} id="op-purchases-settings-tab-0" />
        </Tabs>
        <TabPanel value={tab} index={0}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Alert severity="info" sx={{ mx: 2, mb: 2 }}>
                {translate('operational_purchases.settings_categories_hint')}
              </Alert>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{translate('operational_purchases.settings_category_label')}</TableCell>
                      <TableCell>{translate('operational_purchases.settings_category_code_readonly')}</TableCell>
                      <TableCell align="right">{translate('common.actions')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow key={row.code ? `${row.code}-${idx}` : `row-${idx}`}>
                        <TableCell>
                          <TextField
                            size="small"
                            fullWidth
                            value={row.label}
                            onChange={(e) => {
                              const v = e.target.value;
                              setRows((r) => {
                                const next = [...r];
                                next[idx] = { ...next[idx], label: v };
                                return next;
                              });
                            }}
                          />
                        </TableCell>
                        <TableCell sx={{ maxWidth: 200 }}>
                          {row.code ? (
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                              {row.code}
                            </Typography>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              {translate('operational_purchases.settings_code_after_save')}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            size="small"
                            onClick={() => removeRow(idx)}
                            disabled={rows.length <= 1}
                            aria-label={translate('common.delete')}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 2 }}>
                <Button startIcon={<AddIcon />} onClick={addRow} size="small">
                  {translate('operational_purchases.settings_add_category')}
                </Button>
                <Box sx={{ flex: '1 1 auto' }} />
                <Button variant="contained" onClick={() => save()} disabled={saving}>
                  {translate('common.save')}
                </Button>
              </Box>
            </>
          )}
        </TabPanel>
      </Paper>
      <Dialog
        open={migrationOpen}
        onClose={(_, reason) => {
          if (saving) return;
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            void dismissMigration();
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>{translate('operational_purchases.migration_dialog_title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          <Alert severity="info">{translate('operational_purchases.migration_dialog_hint')}</Alert>
          {pendingMigrations.map((p) => (
            <TextField
              key={p.from_code}
              select
              fullWidth
              label={translate('operational_purchases.migration_target_for', {
                code: p.from_code,
                count: p.count,
              })}
              value={migrationTargets[p.from_code] || ''}
              onChange={(e) =>
                setMigrationTargets((prev) => ({ ...prev, [p.from_code]: e.target.value }))
              }
            >
              <MenuItem value="">
                <em>{translate('operational_purchases.migration_pick')}</em>
              </MenuItem>
              {newCategoryCodes.map((c) => {
                const row = rows.find((r) => String(r.code || '').trim().toUpperCase() === c);
                const label = row?.label ? String(row.label).trim() : '';
                return (
                  <MenuItem key={c} value={c}>
                    {label ? `${c} — ${label}` : c}
                  </MenuItem>
                );
              })}
            </TextField>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !saving && void dismissMigration()}>
            {translate('operational_purchases.migration_cancel')}
          </Button>
          <Button variant="contained" onClick={confirmMigration} disabled={saving}>
            {translate('operational_purchases.migration_confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
